import { useMemo, useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ItemTypes, ItemWithMetaData, RatingTypes } from "../api/Items";
import { useFetchItemsForReview } from "../api/ItemsApi";
import { ReportSummaryItemApi } from "../api/Reports";
import { usePostManualRateDone } from "../api/WorkflowApi";
import Button from "../shared-components/Button";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";

import Pagination from "../shared-components/Pagination";

import { usePermissionValidator } from "../api/UserPermissionValidators";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useReportDetail } from "../reports/ReportDetailProvider";
import { SystemPost } from "./SystemPost";

type Props = {
  isReadOnly: boolean;
  handleExit: () => void;
  report: ReportSummaryItemApi;
  isAmendment: boolean;
};

const SystemRate = ({ isReadOnly, handleExit, report, isAmendment }: Props) => {
  const { search } = useLocation();
  const { canPostRate } = usePermissionValidator();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const { blueprint, isLoadingBlueprint } = useReportDetail();
  const pageSize = 10;
  const [page, setPage] = useState(1);
  const [currentPage, SetCurrentPage] = useState(0);
  const [showNoPostsMessage, setShowNoPostsMessage] = useState(true);
  const [postsCopy, setPostsCopy] = useState<ItemWithMetaData[]>([]);
  const [systemPosts, setSystemPosts] = useState<ItemWithMetaData[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(Number(searchParams.get("page")));
    }
  }, [searchParams]);

  const {
    isLoading: postsLoading,
    data: postList,
    mutate: getPosts,
    isSuccess: postSuccess,
  } = useFetchItemsForReview();
  const {
    isLoading: isLoadingManualRateDone,
    error: errorManualRate,
    isSuccess: isSuccessManualRate,
    mutate: postManualRateDone,
  } = usePostManualRateDone();

  useEffect(() => {
    if (!report?.report_uuid) return;
    if ((!postList && systemPosts.length === 0) || page !== currentPage) {
      getPosts({
        report_uuid: report.report_uuid,
        item_type_name: [ItemTypes.SOCIAL_MEDIA_POST],
        rating_type: RatingTypes.System,
        page_size: pageSize,
        page_num: page,
      });
      SetCurrentPage(page);
    }
  }, [
    getPosts,
    SetCurrentPage,
    currentPage,
    page,
    report?.report_uuid,
    postList,
    systemPosts.length,
  ]);

  useEffect(() => {
    if (!postsLoading && postList?.items) {
      setPostsCopy([...postList.items]);
    }
  }, [postList, postsLoading]);

  useEffect(() => {
    if (postsCopy.length > 0) {
      let posts: ItemWithMetaData[] = postList?.items ? postList?.items : [];
      posts = posts.length === 0 ? postsCopy : posts;
      setSystemPosts([...posts]);
    }
  }, [postsCopy, postList?.items]);

  useEffect(() => {
    if (
      postList?.items.length === 0 &&
      postList.total > 0 &&
      postSuccess &&
      page > 1
    ) {
      setPage(Math.ceil(postList.total / postList.size));
    }
  }, [postList, page, postSuccess]);

  function loadPosts(noPostsMessage: boolean) {
    setShowNoPostsMessage(noPostsMessage);
    getPosts({
      report_uuid: report.report_uuid,
      item_type_name: [ItemTypes.SOCIAL_MEDIA_POST],
      rating_type: RatingTypes.System,
      page_num: page,
      page_size: pageSize,
    });
  }

  useEffect(() => {
    if (isSuccessManualRate) {
      if (isAmendment) {
        navigate("/reports/amendment", { state: { stepDone: true } });
      } else {
        navigate("/reports/next_task", { state: { stepDone: true } });
      }
    }
  }, [isSuccessManualRate, isAmendment, navigate]);

  return (
    <div>
      <div className="flex justify-between items-center pb-6">
        <div className="text-2xl font-extrabold leading-8 tracking-normal text-left">
          System Rated Posts
        </div>
        <div className="flex items-center gap-4">
          <div
            className="text-xs font-medium text-orange-500 cursor-pointer"
            onClick={handleExit}
          >
            Exit Post Rate
          </div>

          {!isReadOnly && canPostRate && (
            <Button
              text="Complete Review"
              onClick={() => {
                if (report.report_uuid)
                  postManualRateDone({
                    reportUUID: report.report_uuid,
                    workflowRunUUID: report.workflow_run_uuid,
                  });
              }}
            />
          )}
        </div>
      </div>
      {errorManualRate && (
        <ErrorMessageContainer
          message={"Error updating finishing the manual rate please try again"}
        />
      )}
      {systemPosts.length === 0 && (
        <div className="flex mt-14 flex-col items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="183"
            height="128"
            viewBox="0 0 183 128"
            fill="none"
          >
            <g clip-path="url(#clip0_1043_62499)">
              <path
                d="M162.31 128H20.8711L41.3247 12.0141H173.23C178.204 12.0141 181.972 16.505 181.108 21.4034L162.31 128Z"
                fill="#E5E7EB"
              />
              <path
                d="M90.4147 7.25213C90.0288 3.14166 86.5783 0 82.4498 0H50.1552C46.2725 0 42.9503 2.78798 42.2766 6.6118L41.3247 12.0141H90.8619L90.4147 7.25213Z"
                fill="#E5E7EB"
              />
              <path
                d="M142.424 22.9397L161.209 127.25H20.9874L2.66999 25.5092C1.8694 21.0625 5.28704 16.9746 9.80527 16.9746H135.289C138.798 16.9746 141.803 19.4868 142.424 22.9397Z"
                fill="#F3F4F6"
                stroke="#E5E7EB"
                strokeWidth="1.5"
              />
            </g>
            <defs>
              <clipPath id="clip0_1043_62499">
                <rect
                  width="182.528"
                  height="128"
                  fill="white"
                  transform="translate(0.236328)"
                />
              </clipPath>
            </defs>
          </svg>
          {showNoPostsMessage && !postsLoading && (
            <>
              <div className="font-semibold  text-gray-900 text-lg mb-6">
                No Posts to rate
              </div>
              <p className="text text-gray-500">
                There are currently no system-rated posts available for rating.
              </p>
            </>
          )}
        </div>
      )}
      <div>
        {systemPosts.map((post) => (
          <SystemPost
            behaviors={blueprint?.behaviors ?? []}
            key={post.item.external_id}
            post={post}
            isReadOnly={isReadOnly || !canPostRate}
            loadPosts={loadPosts}
          />
        ))}
      </div>
      {(isLoadingManualRateDone || postsLoading || isLoadingBlueprint) && (
        <LoadingSpinner />
      )}

      {postList && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={postList?.total ?? 1}
          search={""}
        />
      )}
    </div>
  );
};

export default SystemRate;
